import { SvcPrefigureDiscounts } from "@springtree/eva-services-core";
import { useQuery } from "@tanstack/react-query";
import { Entry } from "contentful";
import { useTranslations } from "next-intl";
import { useMemo } from "react";

import myaccount from "~/components/common/myaccount.module.scss";
import EditorialEntry from "~/components/editorials/editorial-entry";
import { useAuthContext } from "~/contexts/auth";
import { useBreakpoint } from "~/contexts/breakpoint";
import { useEvaService } from "~/hooks/use-eva-service";
import useSubscriptions from "~/hooks/use-subscriptions";
import { isActiveEntry } from "~/utils/editorial-utils";

import RewardsCard from "../rewards-card";
import BirthdayCard from "./birthday-card";
import DataLevelBar from "./data-level-bar";

type Props = {
  editorialComponents?: Entry<unknown>[];
};

export default function LoyaltyProgram(props: Props) {
  const t = useTranslations();
  const breakpoint = useBreakpoint();
  const { user } = useAuthContext();

  const evaServicePrefigureDiscounts = useEvaService(SvcPrefigureDiscounts);

  // understand if the loyalty program is active for this specific organizational unit
  const { hasLoyaltyProgram, userSubscriptions, isUserSubscribedToLoyalty } = useSubscriptions();

  // get data about all the loyalty program tiers
  const { data: prefigureDiscountsResults } = useQuery({
    queryKey: ["evaServicePrefigureDiscounts", user],
    staleTime: Infinity,
    queryFn: async () => {
      const resp = await evaServicePrefigureDiscounts();
      return resp?.Results;
    },
    enabled: !!hasLoyaltyProgram,
  });

  const birthdayPromo = useMemo(
    () => prefigureDiscountsResults?.find((promo) => promo.BackendID?.includes("BIRTHDAY_COUPON")),
    [prefigureDiscountsResults]
  );

  if (!user) {
    return <></>;
  }

  return (
    <>
      {hasLoyaltyProgram ? (
        <>
          {userSubscriptions ? (
            <div className={myaccount.dashboardGrid}>
              <DataLevelBar
                showAnniversaryDate
                enableSeparator={breakpoint === "desktop"}
                className={myaccount.levelBarContainer}
              />
              {/* TODO we should show the birthday gift when requrementsMet are true on the promo */}
              {birthdayPromo?.AvailableUserCoupons && birthdayPromo.AvailableUserCoupons.length > 0 && (
                <BirthdayCard promo={birthdayPromo} />
              )}
              {isUserSubscribedToLoyalty && <RewardsCard onlyAvailable />}
              <div className={myaccount.separator} />
              <div>
                {props.editorialComponents?.filter(isActiveEntry).map((entry) => (
                  <EditorialEntry key={entry.sys.id} entry={entry} />
                ))}
              </div>
            </div>
          ) : (
            <p>{t("generic.notSubscribedToLoyalty")}</p>
          )}
        </>
      ) : (
        <p>{t("generic.noLoyaltyProgramAvailable")}</p>
      )}
    </>
  );
}
